import React from 'react';

// Shared top bar for every game board: score on the left, the current
// question in the middle, and the volume slider + replay on the right -
// the same layout the Tap the Letter game introduced, so every game looks
// and behaves alike.
//
// Volume is owned by the game (each one already scales its own sounds and
// speech by it); this component only renders it and reports changes.
export const GameHeaderBar = ({
  scoreIcon = '🏆',
  scoreLabel = 'Score',
  score,
  total,
  questionLabel,
  question,
  questionStyle,
  volume,
  onVolumeChange,
  onReplay,
  replayTitle = 'Replay',
  replayDisabled = false,
  volumeExtra = null,
  // 'lg' enlarges a sentence-style question ("What goes with the sock?").
  questionSize = 'md',
  className = '',
}) => {
  const pct = Math.round((Number(volume) || 0) * 100);
  const volumeIcon = pct === 0 ? '🔇' : pct < 50 ? '🔉' : '🔊';

  return (
    <div className={`gh-bar ${className}`}>
      <div className="gh-score">
        <span role="img" aria-label="score" className="gh-score-icon">{scoreIcon}</span>
        <span className="gh-score-label">{scoreLabel}</span>
        <span className="gh-score-value">
          {score}{total !== undefined && total !== null ? `/${total}` : ''}
        </span>
      </div>

      <div className={`gh-question ${questionSize === 'lg' ? 'gh-question-lg' : ''}`}>
        {questionLabel ? <span className="gh-question-label">{questionLabel}</span> : null}
        {question !== null && question !== undefined && question !== '' ? (
          <span className="gh-question-value" style={questionStyle}>{question}</span>
        ) : null}
      </div>

      <div className="gh-volume">
        <span role="img" aria-label="volume" className="gh-volume-icon">{volumeIcon}</span>
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={volume}
          aria-label="Volume"
          onChange={(e) => onVolumeChange?.(parseFloat(e.target.value))}
          style={{ '--gh-vol': `${pct}%` }}
        />
        <span className="gh-volume-value">{pct}%</span>
        {onReplay ? (
          <button
            type="button"
            className="gh-replay"
            title={replayTitle}
            aria-label={replayTitle}
            onClick={onReplay}
            disabled={replayDisabled}
          >
            <span className="material-symbols-outlined">replay</span>
          </button>
        ) : null}
        {volumeExtra}
      </div>

      <style>{`
        .gh-bar {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          padding: 0 18px;
          margin-bottom: 18px;
          box-sizing: border-box;
        }
        .gh-score,
        .gh-volume {
          display: flex;
          align-items: center;
          flex-shrink: 0;
          border-radius: 14px;
          padding: 6px 14px;
          min-width: 110px;
          background: rgba(255, 255, 255, 0.86);
          box-shadow: 0 1px 4px rgba(224, 198, 138, 0.10);
          font-weight: 700;
          color: #b8860b;
        }
        .gh-score-icon { font-size: 22px; margin-right: 6px; line-height: 1; }
        .gh-score-label { font-size: 1rem; color: #b8860b; margin-right: 6px; }
        .gh-score-value { font-size: 1.1rem; color: #1976d2; font-weight: 700; margin-left: 4px; }
        .gh-question {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-wrap: wrap;
          min-width: 180px;
          padding: 8px 32px;
          border-radius: 18px;
          background: rgba(224, 198, 138, 0.92);
          box-shadow: 0 1px 4px rgba(224, 198, 138, 0.10);
          text-align: center;
        }
        .gh-question-label { font-size: 1.1rem; font-weight: 600; color: #3e2723; }
        .gh-question-label + .gh-question-value { margin-left: 8px; }
        .gh-question-lg { padding: 8px 28px; }
        .gh-question-lg .gh-question-label { font-size: 1.6rem; font-weight: 800; line-height: 1.25; }
        .gh-question-value {
          font-size: 2rem;
          font-weight: 900;
          color: #1976d2;
          letter-spacing: 2px;
          line-height: 1.2;
          text-shadow: 1px 1px 8px #fff, 0 0 8px #e0c68a;
        }
        .gh-volume { gap: 8px; }
        .gh-volume-icon { font-size: 20px; line-height: 1; }
        .gh-volume input[type=range] {
          width: 90px;
          height: 6px;
          border-radius: 3px;
          cursor: pointer;
          appearance: none;
          -webkit-appearance: none;
          background: linear-gradient(90deg, #1976d2 var(--gh-vol), #d6dbe3 var(--gh-vol));
        }
        .gh-volume input[type=range]::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 16px; height: 16px; border-radius: 50%;
          background: #1976d2; border: 2px solid #fff; box-shadow: 0 1px 3px rgba(0,0,0,.3);
        }
        .gh-volume input[type=range]::-moz-range-thumb {
          width: 14px; height: 14px; border-radius: 50%; background: #1976d2; border: 2px solid #fff;
        }
        .gh-volume-value { min-width: 40px; text-align: right; font-size: 1rem; color: #1976d2; font-weight: 700; }
        .gh-replay {
          display: inline-flex; align-items: center; justify-content: center;
          border-radius: 12px; padding: 2px 8px;
          border: 1px solid #6c757d; background: transparent; color: #6c757d; cursor: pointer;
        }
        .gh-replay:hover:not(:disabled) { background: #6c757d; color: #fff; }
        .gh-replay:disabled { opacity: 0.5; cursor: default; }
        .gh-replay .material-symbols-outlined { font-size: 20px; }
        @media (max-width: 700px) {
          .gh-bar { flex-direction: column; gap: 10px; padding: 0 4px; margin-bottom: 10px; }
          .gh-question { width: 100%; min-width: 0; padding: 8px 12px; }
          .gh-question-value { font-size: 1.5rem; }
          .gh-question-lg .gh-question-label { font-size: 1.3rem; }
        }
        @media (max-width: 480px) {
          .gh-volume input[type=range] { width: 70px; }
        }
      `}</style>
    </div>
  );
};

export default GameHeaderBar;
